/**
 * Sentry, as its own entry — not a chunk of the app's.
 *
 * index.html loads this as a second module script beside main.jsx.
 * Nothing in the app imports it, and it imports nothing of the app, so
 * there is no edge for a bundler to merge: the boot bundle carries no
 * Sentry whatever the build does with the SDK's re-exports. That
 * matters because it has happened twice. First Vercel's build kept
 * @sentry/browser's session-replay and feedback re-exports in the
 * entry (190 KB nobody asked for) when the local build shook them
 * out; then, with the SDK behind a dynamic import, Vercel folded the
 * whole SDK chunk back into the entry while the local build split it.
 * A budget on the entry cannot depend on which of those a bundler
 * does. Two scripts with no import between them is the one layout
 * that settles it.
 *
 * @sentry/browser, not @sentry/react: the React package adds only a
 * boundary and router instrumentation, neither used here, and
 * importing it would pull React into this entry and have the bundler
 * carve a chunk the two entries share — which is the main bundle's
 * weight moving around again.
 *
 * Timing: both scripts download in parallel and run in document
 * order, main.jsx first. An error thrown before this one runs is kept
 * by main.jsx on window.__earlyErrors and reported from here, late
 * rather than lost.
 */
import { init, browserTracingIntegration, addIntegration, captureException } from '@sentry/browser';

// Scrub anything that looks like PII / secrets out of outbound error
// reports — and a session replay can capture form input that
// ordinary error events never see, which is why REPLAY_OPTIONS uses
// replayIntegration's mask/block options to hide every <input> by
// default rather than relying on this string pass alone.
const SENSITIVE_STRING_PATTERNS = [
  /[\w.+-]+@[\w-]+\.[\w.-]+/g,           // emails
  /Bearer\s+[A-Za-z0-9._\-+/=]{20,}/g,    // bearer tokens
  /eyJ[A-Za-z0-9._\-+/=]{20,}/g,          // JWTs
  /sk_(?:test|live)_[A-Za-z0-9]{16,}/g,   // API key shapes
];
export function redactString(s) {
  if (typeof s !== 'string') return s;
  let out = s;
  for (const re of SENSITIVE_STRING_PATTERNS) out = out.replace(re, '[redacted]');
  return out;
}
export function deepRedact(value, depth = 0) {
  if (depth > 5 || value == null) return value;
  if (typeof value === 'string') return redactString(value);
  if (Array.isArray(value)) return value.map((v) => deepRedact(v, depth + 1));
  if (typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = deepRedact(v, depth + 1);
    return out;
  }
  return value;
}

// Session replay masks everything. Before this was set, session
// replays were capturing whatever the user typed (passwords, account
// numbers, a seller's phone) in the clear.
const REPLAY_OPTIONS = { maskAllText: true, maskAllInputs: true, blockAllMedia: true };

export function beforeSend(event) {
  if (event.message) event.message = redactString(event.message);
  if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map((b) => ({
    ...b,
    message: b.message ? redactString(b.message) : b.message,
    data: deepRedact(b.data),
  }));
  if (event.extra) event.extra = deepRedact(event.extra);
  if (event.exception?.values) {
    for (const v of event.exception.values) {
      if (v.value) v.value = redactString(v.value);
    }
  }
  return event;
}

/**
 * Start Sentry. `earlyErrors` is what main.jsx caught before this ran;
 * each is reported now. Returns the one function the app's error
 * boundary needs, which main.jsx reads off window.__sentryCapture.
 */
export function bootSentry({ dsn, earlyErrors = [] } = {}) {
  init({
    dsn,
    // The commit Vercel built and the environment it built for, both
    // defined at build time in vite.config.js. The same release name
    // the source maps were uploaded under, which is how a minified
    // frame finds its map.
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || undefined,
    integrations: [
      browserTracingIntegration(),
    ],
    tracesSampleRate: 0.2,
    // Session replay is NOT here. Referenced statically it is a third of
    // the SDK; it is loaded below, after the page is idle.
    replaysSessionSampleRate: 0.0,
    // Was 1.0 — every error session shipped a full replay. Drop to 0.1
    // so a bad deploy does not also mean a replay bill.
    replaysOnErrorSampleRate: 0.1,
    enabled: !!dsn,
    beforeSend,
  });

  for (const err of earlyErrors) {
    try { captureException(err); } catch { /* a report that cannot be sent is not a second error */ }
  }

  // Session replay, after first paint. The same masking as before; the
  // only thing that changes is when the recorder arrives. The first
  // second or two of a session goes without a replay, which is the
  // trade for a boot bundle a third smaller.
  const loadReplay = () => import('@sentry-internal/replay')
    .then(({ replayIntegration }) => { addIntegration(replayIntegration(REPLAY_OPTIONS)); })
    .catch(() => { /* a replay that will not load is not an error worth a dialog */ });
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) window.requestIdleCallback(loadReplay, { timeout: 4000 });
  else setTimeout(loadReplay, 1500);

  return { captureException };
}

// Self-starting, behind the DSN. Without one this entry is a no-op the
// size of this file; the SDK import above is still fetched, which only
// a build without a DSN — a preview, a local run — ever pays for.
if (typeof window !== 'undefined' && import.meta.env.VITE_SENTRY_DSN) {
  const queue = Array.isArray(window.__earlyErrors) ? window.__earlyErrors : [];
  const sentry = bootSentry({ dsn: import.meta.env.VITE_SENTRY_DSN, earlyErrors: queue.splice(0) });
  window.__sentryCapture = (err) => { try { sentry.captureException(err); } catch { /* see above */ } };
}
